import { ValidatorFn, AbstractControl } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Interval } from "../constants/interval";
import { HOURS_PER_DAY, MINUTES_PER_HOUR, SECONDS_PER_DAY, SECONDS_PER_HOUR, SECONDS_PER_MINUTE } from "../constants/time.const";

const NR_OF_MILLISECONDS_PER_MINUTE: number = 60000;
const NR_OF_MILLISECONDS_PER_HOUR: number = NR_OF_MILLISECONDS_PER_MINUTE * 60;

export function dateValidatorPast(): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    const today: Date = new Date();
    today.setHours(0, 0, 0, 0);   

    if(!(control && control.value)) {
      // if there's no control or no value, that's ok
      return null;
    }

    // return null if there's no errors
    return new Date(control.value).getTime() < today.getTime()
      ? {invalidDate: 'You cannot use past dates' } 
      : null;
  }
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

export function isSameHour(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate() &&
    a.getHours() === b.getHours();
}

export function isToday(date: Date): boolean {
  const today: Date = new Date();

  return date.getDate() == today.getDate() &&
    date.getMonth() == today.getMonth() &&
    date.getFullYear() == today.getFullYear();
}

export function isThisMonth(date: Date): boolean {
  const today: Date = new Date();

  return date.getMonth() == today.getMonth() &&
    date.getFullYear() == today.getFullYear();
}

export function isThisYear(date: Date): boolean {
  return date.getFullYear() == new Date().getFullYear();
}

export function fromMilliseconds(milliseconds: number): Date {
  let date: Date = new Date();

  const seconds: number = Math.floor((milliseconds / 1000) % 60);
  const minutes: number = Math.floor((milliseconds / (1000 * 60)) % 60);
  const hours: number = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);

  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);

  return date;
}

export function toMilliseconds(hours: number, minutes: number): number {
  return hours * NR_OF_MILLISECONDS_PER_HOUR + minutes * NR_OF_MILLISECONDS_PER_MINUTE;
}

export function toInterval(date: Date, interval: Interval): Date {
  let alignedDate: Date = new Date(date);
  switch (interval) {
    case Interval.Daily: {
      alignedDate.setHours(0);
    }
    case Interval.Hourly: {
      alignedDate.setSeconds(0);
      alignedDate.setMinutes(0);
      alignedDate.setMilliseconds(0);
      break;
    }
    default: {
      throw new Error('Interval not implemented');
    }
  }
  return alignedDate;
}

export function toISODateOnly(date: Date): string {
  const day: string = date.getDate().toString().padStart(2, '0');
  const month: string = (date.getMonth() + 1).toString().padStart(2, '0');
  const year: string = date.getFullYear().toString().padStart(4, '0');

  return `${year}-${month}-${day}`;
}

export function toISOTimeOnly(date: Date): string {
  const hours: string = date.getHours().toString().padStart(2, '0');
  const minutes: string = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
}

/**
 * Create an ISO string of a Date that does not convert to UTC and does not include a timezone offset
 * @param date The date to convert
 * @returns ISO string containing date and time with second precision
 */
export function toISOLocal(date: Date): string {
  const day: string = date.getDate().toString().padStart(2, '0');
  const month: string = (date.getMonth() + 1).toString().padStart(2, '0');
  const year: string = date.getFullYear().toString().padStart(4, '0');
  const hours: string = date.getHours().toString().padStart(2, '0');
  const minutes: string = date.getMinutes().toString().padStart(2, '0');
  const seconds: string = date.getSeconds().toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Checks if a timestamp is in a local time with a timezone offset
 */
export function isLocalTimestamp(timestamp: string): boolean {
  return timestamp.includes('+');
}

/**
 * Checks if a timestamp does not have any timezone information
 */
export function isAmbiguousTimestamp(timestamp: string): boolean {
  return !isUTCTimestamp(timestamp) && !isLocalTimestamp(timestamp);
}

/**
 * Checks if a timestamp is in UTC with a Z timezone offset
 */
export function isUTCTimestamp(timestamp: string): boolean {
  return timestamp.includes('Z');
}

/**
 * Removes the timezone offset from a timestamp 
 */
export function stripTimezoneInfo(timestamp: string): string {
  const end: number = timestamp.lastIndexOf('+');
  if (end === -1) {
    if (timestamp.endsWith('Z')) {
      return timestamp.substring(0, timestamp.length - 1);
    }
    return timestamp;
  }
  return timestamp.substring(0, end);
}

export function getStartOfDay(date: Date): Date {
  date.setHours(0, 0, 0, 0);
  return date;
}

export function getEndOfDay(date: Date): Date {
  date.setHours(23, 59, 59, 999);
  return date;
}

export function getTimezoneOffsetInMinutes(isoString: string): number {
  if (isAmbiguousTimestamp(isoString)) {
    return 0;
  }

  const suffix: string = getTimezoneSuffix(isoString);
  if (suffix === 'Z') {
    return 0;
  }

  const parts: string[] = suffix.split(':');
  const hours: number = Number.parseInt(parts[0], 10);
  const minutes: number = Number.parseInt(parts[1], 10);
  return hours * 60 + (minutes * Math.sign(hours));
}

export function toIsoStringWithTimezoneOffset(date: Date): string {
  const timezoneOffset: number = -date.getTimezoneOffset(),
        dif = timezoneOffset >= 0 ? '+' : '-',
        pad = function(num: number) {
            const norm: number = Math.floor(Math.abs(num));
            return (norm < 10 ? '0' : '') + norm;
        };
  
    return date.getFullYear() +
        '-' + pad(date.getMonth() + 1) +
        '-' + pad(date.getDate()) +
        'T' + pad(date.getHours()) +
        ':' + pad(date.getMinutes()) +
        ':' + pad(date.getSeconds()) +
        dif + pad(timezoneOffset / 60) +
        ':' + pad(timezoneOffset % 60);
}

export function isBetween(date: Date, from: Date, until: Date): boolean {
  // Same range check as XPAC uses
  return date >= from && date < until;
}

export function getTimezoneSuffix(isoDateString: string): string {
  if (isoDateString.endsWith('Z')) {
    return 'Z';
  }
  return isoDateString.slice(isoDateString.length - 6);
}

export enum DurationPrecision
{
  Days,
  Hours,
  Minutes,
  Seconds,
  Milliseconds
}

export function getDurationString(totalSeconds: number, translate: TranslateService, precision: DurationPrecision = DurationPrecision.Seconds, maxParts: number = Number.MAX_VALUE): string {
  let partsAdded: number = 0;
  let result: string = '';

  function appendDurationPart(amount: number, type: DurationPrecision, translationKey: string): void {
    if (type > precision) {
      return;
    }
    
    if (partsAdded >= maxParts) {
      return;
    }

    if (amount === 0 && !(type === precision && result.length === 0)) {
      return;
    }
  
    if (result.length > 0) {
      result += ', ';
    }
  
    result += translate.instant(`General.DurationParts.${translationKey}`, { amount });
    partsAdded++;
  }

  if (totalSeconds < 1 && precision === DurationPrecision.Milliseconds) {
    appendDurationPart(Math.floor(totalSeconds * 1000), DurationPrecision.Milliseconds, 'Milliseconds');
    return result;
  }

  const days: number = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const hours: number = Math.floor(totalSeconds / SECONDS_PER_HOUR) % HOURS_PER_DAY;
  const minutes: number = Math.floor(totalSeconds / SECONDS_PER_MINUTE) % MINUTES_PER_HOUR;
  const seconds: number = Math.floor(totalSeconds % SECONDS_PER_MINUTE);

  appendDurationPart(days, DurationPrecision.Days, days !== 1 ? 'Days' : 'Day');
  appendDurationPart(hours, DurationPrecision.Hours, hours !== 1 ? 'Hours' : 'Hour');
  appendDurationPart(minutes, DurationPrecision.Minutes, minutes !== 1 ? 'Minutes' : 'Minute');
  appendDurationPart(seconds, DurationPrecision.Seconds, seconds !== 1 ? 'Seconds' : 'Second');
  
  return result;
}

